import React, { FC } from 'react';
import { View } from 'react-native';
import { scale } from '@components/frame';
import { font } from '@styles/fonts';
import BlockWrapper from '@v2/pages/machines/modules/now/components/Overview/components/BlockWrapper';
import { TourTarget } from '@components/tours';
import { WELCOME_TOUR_IDS } from '@components/tours/config/global/welcome/constants';
import { MACHINES_NOW_OVERVIEW_BLOCK_CONFIG, NC_FILE_MIN_VISIBLE_ROWS } from '@v2/pages/machines/modules/now/components/Overview/constants';
import NcFileContent from '@components/common/NCFile';
import InputLine from '@components/machines/components/detailsComponents/activeMachine/components/InputLine';
import FeatureFlags from '@components/featureFlags';
import { NCFileLevelSubScreen } from '@v2/pages/nc-file/details/common/enums/ncFileDetails.enum';
import { ShowFullNcFileOverlay } from '@v2/elements/ShowFullNcFileButton/ShowFullNcFileOverlay';
import TouchableElement from '@components/common/touchableElement';
import { WithColor } from '@styles/colors';
import { useManageNCFileBlock } from './hooks/useManageNCFileBlock';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';

export interface NcFileBlockProps extends WithColor {
  largeMode?: boolean;
  largeHeight?: number;
}

const NcFileBlock: FC<NcFileBlockProps> = ({ C, largeMode = false, largeHeight }) => {
  const {
    selectedMachine,
    ncFileId,
    ncFileName,
    versionNumber,
    handleNCFileNavigation,
    shouldShowOverlay,
    setShouldShowOverlay,
    baseColor,
    NcFileContentWrapper,
    tourStyle,
    batchLoaded,
  } = useManageNCFileBlock({ C, largeMode, largeHeight });

  if (!ncFileName) return null;

  return (
    <TourTarget tourId={WELCOME_TOUR_IDS.LIVE_NC_FILE_BLOCK} style={tourStyle}>
      <BlockWrapper testID={TEST_IDS.liveScreen.ncFileBlock} containerStyle={{ overflow: 'hidden', paddingHorizontal: 0, flex: 1 }}>
        <View style={{ overflow: 'hidden' }}>
          {Boolean(versionNumber) && (
            <FeatureFlags name="ncfile-versions">
              <InputLine
                testID={TEST_IDS.liveScreen.ncFileBlockVersions}
                bg={MACHINES_NOW_OVERVIEW_BLOCK_CONFIG.blockBgColor}
                font={font}
                scale={scale}
                data={[]}
                index={1}
                i={{
                  action: batchLoaded ? () => handleNCFileNavigation(NCFileLevelSubScreen.VERSIONS) : undefined,
                  name: 'Version',
                  value: versionNumber,
                  isText: true,
                }}
                C={C}
              />
            </FeatureFlags>
          )}
        </View>

        <TouchableElement
          testID={TEST_IDS.liveScreen.ncFileBlockContent}
          disabled={!batchLoaded}
          onPress={() => handleNCFileNavigation(NCFileLevelSubScreen.GENERAL)}
        >
          {({ boxStyle }) => (
            <View style={[boxStyle, { flex: 1 }]}>
              <NcFileContentWrapper style={{ flex: 1 }}>
                <View pointerEvents="none">
                  <NcFileContent
                    selectedItem={selectedMachine}
                    C={C}
                    shouldTruncate={!largeMode}
                    truncateLines={NC_FILE_MIN_VISIBLE_ROWS}
                    shouldScroll={false}
                    backgroundColor={MACHINES_NOW_OVERVIEW_BLOCK_CONFIG.blockBgColor}
                    shouldTakeNCFileDataFromMachine
                    onlyNCFileContent
                    singleColor
                    ncFileTextColor={C('Label/Light/Primary')}
                    withoutCollapsingFeatureAndStyles
                    setShouldShowGlobalFullNCFileButton={setShouldShowOverlay}
                  />
                </View>
              </NcFileContentWrapper>
              {shouldShowOverlay && <ShowFullNcFileOverlay C={C} ncFileId={ncFileId} ncFileName={ncFileName} baseColor={baseColor} />}
            </View>
          )}
        </TouchableElement>
      </BlockWrapper>
    </TourTarget>
  );
};

export default NcFileBlock;
